import { Grid, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { AttachMoney } from '@mui/icons-material';
import FormHeading from 'common/FormHeading';
import FormInputLabel from 'common/FormInputLabel';
import FormSelectInput from 'common/FormSelectInput';
import FormTextInput from 'common/FormTextInput';
import { serviceTimeOptions } from 'common/constant';
import Services from 'pages/Business/Provider/ProviderPopup/Partials/Services';
import { theme } from 'theme';

import { useEffect, useState, useRef } from 'react';
import { useAuth } from 'context/AuthContext';

export default function ServiceForm({
    formData,
    formState,
    setAdditionalMultipleFields,
    additionalMultipleFields,
    businessId,
    consulationDetails = {},
    setConsulationDetails,
    handleChange,
    onAddService,
    onBeforeSubmit,
    onConsultationCheckboxesChange,
    consultationPriceErrors = {},
    onClearConsultationPriceError,
}) {
    const [extraServicesCount, setExtraServicesCount] = useState(0);

    const [extraService, setExtraService] = useState([]);
    const [extraServiceDuration, setExtraServiceDuration] = useState([]);
    const [extraServicePrice, setExtraServicePrice] = useState([]);
    const [extraServiceTelemedecine, setExtraServiceTelemedecine] = useState([false]);
    const [extraServiceBookable, setExtraServiceBookable] = useState([true]);

    const [servicesOptions, setServicesOptions] = useState();
    const [addTelemedicine, setAddTelemedicine] = useState(false);
    const [addInPerson, setAddInPerson] = useState(false);

    const { token } = useAuth();

    useEffect(() => {
        if (!businessId || !token) return;
        fetch(`${process.env.REACT_APP_SERVER_URL}/api/providers/services`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ business_id: businessId }),
        })
            .then((res) => res.json())
            .then((result) => {
                if (result.success && result.data) {
                    const services = Array.isArray(result.data) ? result.data : [result.data];
                    setServicesOptions(
                        services.map((s) => ({ id: s.id, service: s.name, price: s.price }))
                            .concat([{ id: 'add_new_service', service: '+ Add New Service', price: null }])
                    );
                }
            })
            .catch((err) => console.error('Error fetching business services:', err));
    }, [businessId, additionalMultipleFields?.refreshServices]);
    const [currentServiceIndex, setCurrentServiceIndex] = useState(null);
    const userUncheckedTelemedicineRef = useRef(false); // Track if user just unchecked to prevent auto-re-enable
    const userUncheckedInPersonRef = useRef(false); // Track if user just unchecked in-person to prevent auto-re-enable
    const consultationTogglesInitializedRef = useRef(false); // Ensure we only auto-set addInPerson/addTelemedicine once per mount

    // Handle adding a new service
    const handleAddService = (serviceIndex) => {
        setCurrentServiceIndex(serviceIndex);
        if (onAddService) {
            onAddService(serviceIndex);
        }
    };

    const handelServiceChange = (index) => {
        return (e) => {
            let { name, value, checked } = e.target;

            if (name === 'service') {
                let services = Array.isArray(extraService) ? [...extraService] : [];
                // Ensure value is stored consistently (keep as string from select, but handle number IDs)
                services[index] = value;
                setExtraService(services);

                // If duration hasn't been set yet for this service, default to 15 minutes
                let serviceDuration = Array.isArray(extraServiceDuration) ? [...extraServiceDuration] : [];
                if (!serviceDuration[index] && serviceDuration[index] !== 0 && serviceDuration[index] !== '0') {
                    serviceDuration[index] = 15;
                    setExtraServiceDuration(serviceDuration);
                    setAdditionalMultipleFields((prev) => ({
                        ...prev,
                        service_duration: serviceDuration,
                        form_section: 'services',
                    }));
                }

                setAdditionalMultipleFields((prev) => ({
                    ...prev,
                    services: services,
                    form_section: 'services',
                }));
            }

            if (name === 'service_duration') {
                let serviceDuration = Array.isArray(extraServiceDuration) ? [...extraServiceDuration] : [];
                serviceDuration[index] = value;
                setExtraServiceDuration(serviceDuration);

                setAdditionalMultipleFields((prev) => ({
                    ...prev,
                    service_duration: serviceDuration,
                    form_section: 'services',
                }));
            }

            if (name === 'service_price') {
                let servicePrice = Array.isArray(extraServicePrice) ? [...extraServicePrice] : [];
                servicePrice[index] = value;
                setExtraServicePrice(servicePrice);

                setAdditionalMultipleFields((prev) => ({
                    ...prev,
                    service_price: servicePrice,
                    form_section: 'services',
                }));
            }
            if (name === 'service_telemedecine') {
                let serviceTelemedicine = Array.isArray(extraServiceTelemedecine) ? [...extraServiceTelemedecine] : [];
                serviceTelemedicine[index] = checked;
                setExtraServiceTelemedecine(serviceTelemedicine);

                setAdditionalMultipleFields((prev) => ({
                    ...prev,
                    service_telemedecine: serviceTelemedicine,
                    form_section: 'services',
                }));
            }
            if (name === 'service_bookable') {
                let serviceBookable = Array.isArray(extraServiceBookable) ? [...extraServiceBookable] : [];
                serviceBookable[index] = checked;
                setExtraServiceBookable(serviceBookable);

                setAdditionalMultipleFields((prev) => ({
                    ...prev,
                    service_bookable: serviceBookable,
                    form_section: 'services',
                }));
            }
        };
    };

    // Function to handle service price auto-population
    const handleServicePriceAutoPopulate = (index, price) => {
        let servicePrice = Array.isArray(extraServicePrice) ? [...extraServicePrice] : [];
        
        // Format price to always show .00 for whole numbers
        // Service prices are stored in dollars (not cents like consultation prices)
        let formattedPrice = '';
        if (price !== null && price !== undefined && price !== '') {
            // Convert to number and format with 2 decimal places
            const priceNum = typeof price === 'number' ? price : parseFloat(price);
            if (!isNaN(priceNum)) {
                formattedPrice = priceNum.toFixed(2);
            } else {
                formattedPrice = String(price);
            }
        }
        
        servicePrice[index] = formattedPrice;
        setExtraServicePrice(servicePrice);

        setAdditionalMultipleFields((prev) => ({
            ...prev,
            service_price: servicePrice,
            form_section: 'services',
        }));
    };

    const handleConsulationChange = (e) => {
        let { name, value } = e.target;
        // Convert to number for proper storage
        const numValue = Number(value);
        setConsulationDetails({
            ...consulationDetails,
            [name]: numValue,
        });
    };

    const handleToggleInPerson = () => {
        const newAddInPerson = !addInPerson;
        setAddInPerson(newAddInPerson);
        
        // Persist the user's explicit choice so we can restore it when
        // navigating away from and back to the Services step in add mode.
        if (setAdditionalMultipleFields) {
            setAdditionalMultipleFields((prev) => ({
                ...prev,
                addInPersonConsultation: newAddInPerson,
            }));
        }
        
        // Notify parent of checkbox state change
        if (onConsultationCheckboxesChange) {
            onConsultationCheckboxesChange({ addInPerson: newAddInPerson, addTelemedicine });
        }

        if (newAddInPerson === false) {
            // User is unchecking - set flag to prevent auto-re-enable for the rest of this editing session
            userUncheckedInPersonRef.current = true;
            
            // Clear in-person consultation details
            setConsulationDetails({
                ...consulationDetails,
                in_person_consultation_price: '',
                in_person_consultation_duration: 0,
            });
        } else {
            // When enabling in-person, clear the flag and initialize default values
            userUncheckedInPersonRef.current = false;
            
            // Initialize with default duration if not already set
            const currentDuration = consulationDetails?.in_person_consultation_duration;
            const currentPrice = consulationDetails?.in_person_consultation_price;
            
            // Only set defaults if values are empty/zero
            if (!currentDuration || currentDuration === 0) {
                setConsulationDetails({
                    ...consulationDetails,
                    in_person_consultation_duration: 15,
                    in_person_consultation_price: currentPrice || ''
                });
            }
        }
    };

    const handleToggleTelemedicine = () => {
        const newAddTelemedicine = !addTelemedicine;
        setAddTelemedicine(newAddTelemedicine);
        
        // Persist the user's explicit choice so we can restore it when
        // navigating away from and back to the Services step in add mode.
        if (setAdditionalMultipleFields) {
            setAdditionalMultipleFields((prev) => ({
                ...prev,
                addTelemedicineConsultation: newAddTelemedicine,
            }));
        }
        
        // Notify parent of checkbox state change
        if (onConsultationCheckboxesChange) {
            onConsultationCheckboxesChange({ addInPerson, addTelemedicine: newAddTelemedicine });
        }

        if (newAddTelemedicine === false) {
            // User is unchecking - set flag to prevent auto-re-enable
            userUncheckedTelemedicineRef.current = true;
            
            // User is unchecking - clear telemedicine consultation details
            // Update consulationDetails to clear telemedicine data
            setConsulationDetails({
                ...consulationDetails,
                telemedicine_consultation_price: '',
                telemedicine_consultation_duration: 0,
                telemedicine_url: '' // Also clear URL in consulationDetails
            });
            
            // Clear telemedicine URL from formData
            handleChange({
                target: {
                    name: 'telemedicine_url',
                    value: ''
                }
            });
            
            // Reset flag after a short delay to allow state to settle
            setTimeout(() => {
                userUncheckedTelemedicineRef.current = false;
            }, 100);
            
            // Reset all service_telemedecine flags to false (but keep them, just don't enable them)
            // Only reset if telemedicine is being disabled completely
        } else {
            // When enabling telemedicine, clear the flag and update service flags
            userUncheckedTelemedicineRef.current = false;
            setExtraServiceTelemedecine((prevList) => 
                Array.isArray(prevList) && prevList.length > 0 
                    ? prevList.map(() => true) 
                    : [true]
            );
            
            // Initialize with default duration if not already set
            const currentDuration = consulationDetails?.telemedicine_consultation_duration;
            const currentPrice = consulationDetails?.telemedicine_consultation_price;
            const currentUrl = consulationDetails?.telemedicine_url;
            
            // Only set defaults if values are empty/zero
            if (!currentDuration || currentDuration === 0) {
                setConsulationDetails({
                    ...consulationDetails,
                    telemedicine_consultation_duration: 15,
                    telemedicine_consultation_price: currentPrice || '',
                    telemedicine_url: currentUrl || ''
                });
            }
        }
    };
    
    useEffect(() => {
        setAdditionalMultipleFields((prev) => ({
            ...prev,
            form_section: 'services',
        }));
    }, []);
    
    // Initialize consultation details with default 15 minutes for new providers
    // Only set duration defaults, but let user decide whether to enable consultations
    useEffect(() => {
        if (formState === 'add') {
            // Always ensure default durations are set when in add mode
            // This prevents issues where the dropdown shows 15 but state has 0
            setConsulationDetails(prev => {
                // Only update if not already properly initialized
                const needsInit = !prev || 
                                 Object.keys(prev).length === 0 || 
                                 prev.in_person_consultation_duration === 0 || 
                                 prev.telemedicine_consultation_duration === 0;
                
                if (needsInit) {
                    return {
                        in_person_consultation_duration: 15,
                        in_person_consultation_price: prev?.in_person_consultation_price || '',
                        telemedicine_consultation_duration: 15,
                        telemedicine_consultation_price: prev?.telemedicine_consultation_price || '',
                        telemedicine_url: prev?.telemedicine_url || ''
                    };
                }
                return prev;
            });
        }
    }, [formState]);
    
    // Expose function to sync current state to additionalMultipleFields when needed
    useEffect(() => {
        if (onBeforeSubmit) {
            const syncFunction = () => {
                // Sync current service state AND consultation details to additionalMultipleFields
                const currentState = {
                    services: extraService,
                    service_price: extraServicePrice,
                    service_duration: extraServiceDuration,
                    service_telemedecine: extraServiceTelemedecine,
                    service_bookable: extraServiceBookable,
                    form_section: 'services',
                    // Also sync consultation details
                    ...consulationDetails
                };
                setAdditionalMultipleFields((prev) => ({
                    ...prev,
                    ...currentState
                }));
                return currentState;
            };
            onBeforeSubmit(syncFunction);
        }
    }, [extraService, extraServicePrice, extraServiceDuration, extraServiceTelemedecine, extraServiceBookable, consulationDetails, onBeforeSubmit, setAdditionalMultipleFields]);

    useEffect(() => {
        if (formState === 'edit' && formData && Object.keys(formData).length > 0) {
            // Skip updating telemedicine checkbox state if user is actively typing in URL field
            // We'll check consulationDetails which updates immediately when typing
            const isTypingInUrlField = consulationDetails?.telemedicine_url && consulationDetails.telemedicine_url.length > 0 && 
                                       (!formData?.telemedicine_url || formData.telemedicine_url.length === 0);
            // Check both formData and additionalMultipleFields for services data
            // Convert service IDs to strings for consistency with FormSelectInput
            const servicesFromFormData = Array.isArray(formData.services) ? formData.services.map(s => String(s)) : [];
            const servicesFromAdditional = Array.isArray(additionalMultipleFields?.services) ? additionalMultipleFields.services.map(s => String(s)) : [];
            const services = servicesFromFormData.length > 0 ? servicesFromFormData : servicesFromAdditional;
            const serviceDuration = Array.isArray(formData.service_duration) ? formData.service_duration : 
                                  (Array.isArray(additionalMultipleFields?.service_duration) ? additionalMultipleFields.service_duration : []);
            const servicePrice = Array.isArray(formData.service_price) ? formData.service_price : 
                               (Array.isArray(additionalMultipleFields?.service_price) ? additionalMultipleFields.service_price : []);
            const serviceTelemedicine = Array.isArray(formData.service_telemedecine) ? formData.service_telemedecine : 
                                      (Array.isArray(additionalMultipleFields?.service_telemedecine) ? additionalMultipleFields.service_telemedecine : []);
            const serviceBookable = Array.isArray(formData.service_bookable) ? formData.service_bookable : 
                                  (Array.isArray(additionalMultipleFields?.service_bookable) ? additionalMultipleFields.service_bookable : []);
            const serviceVisitMode = Array.isArray(formData.service_visit_mode) ? formData.service_visit_mode : 
                                   (Array.isArray(additionalMultipleFields?.service_visit_mode) ? additionalMultipleFields.service_visit_mode : []);
            
            // Find maximum length across all arrays to preserve incomplete services
            // (services with duration/price/availability but no service name)
            const maxLength = Math.max(
                services.length,
                serviceDuration.length || 0,
                servicePrice.length || 0,
                serviceTelemedicine.length || 0,
                serviceBookable.length || 0,
                serviceVisitMode.length || 0
            );
            
            // Pad services array with empty strings for incomplete services to preserve them
            const paddedServices = [...services];
            while (paddedServices.length < maxLength) {
                paddedServices.push('');
            }
            
            // Only initialize if we have services data or if services haven't been initialized yet
            if (paddedServices.length > 0 || !extraService || extraService.length === 0) {
                // Always populate services when in edit mode
                const currentTelemedicineState = formData?.telemedicine_url && formData.telemedicine_url.length > 0;
                
                const finalServiceTelemedicine = serviceTelemedicine.length === 0 && services.length > 0 ? services.map(() => currentTelemedicineState) : 
                                               serviceTelemedicine.length === 0 ? [currentTelemedicineState] : serviceTelemedicine;
                const finalServiceBookable = serviceBookable.length === 0 && services.length > 0 ? services.map(() => true) : 
                                           serviceBookable.length === 0 ? [true] : serviceBookable;
                
                // Derive visit modes if not available: use service_visit_mode if present, otherwise derive from telemedicine flags
                const finalServiceVisitMode = serviceVisitMode.length > 0 ? serviceVisitMode :
                    paddedServices.map((_, index) => {
                        const isBookable = finalServiceBookable[index] !== undefined ? finalServiceBookable[index] : true;
                        const isTelemedicine = finalServiceTelemedicine[index] || false;
                        if (!isBookable) return 'not_available';
                        if (isTelemedicine) return 'both'; // If telemedicine is true, assume 'both' for backward compatibility
                        return 'in_person';
                    });
                
                // Pad other arrays to match maxLength to preserve incomplete services
                const paddedServiceDuration = [...serviceDuration];
                while (paddedServiceDuration.length < maxLength) {
                    paddedServiceDuration.push('');
                }
                const paddedServicePrice = [...servicePrice];
                while (paddedServicePrice.length < maxLength) {
                    paddedServicePrice.push('');
                }
                const paddedServiceTelemedicine = [...finalServiceTelemedicine];
                while (paddedServiceTelemedicine.length < maxLength) {
                    paddedServiceTelemedicine.push(false);
                }
                const paddedServiceBookable = [...finalServiceBookable];
                while (paddedServiceBookable.length < maxLength) {
                    paddedServiceBookable.push(true);
                }
                const paddedServiceVisitMode = [...finalServiceVisitMode];
                while (paddedServiceVisitMode.length < maxLength) {
                    paddedServiceVisitMode.push('in_person');
                }
                
                setExtraServicesCount(paddedServices.length > 0 ? paddedServices.length - 1 : 0);
                setExtraService(paddedServices);
                setExtraServiceDuration(paddedServiceDuration);
                setExtraServicePrice(paddedServicePrice);
                setExtraServiceTelemedecine(paddedServiceTelemedicine);
                setExtraServiceBookable(paddedServiceBookable);
                
                // CRITICAL: Update additionalMultipleFields with all service data to ensure it's available for submission
                // Use padded arrays to preserve incomplete services
                setAdditionalMultipleFields(prev => ({
                    ...prev,
                    services: paddedServices,
                    service_price: paddedServicePrice,
                    service_duration: paddedServiceDuration,
                    service_telemedecine: paddedServiceTelemedicine,
                    service_bookable: paddedServiceBookable,
                    service_visit_mode: paddedServiceVisitMode,
                    form_section: 'services',
                }));
                
                // Initialize consultation details from formData if available
                // Only initialize once when formData is first loaded, not on every formData change
                if (formData && (
                    formData.in_person_consultation_duration !== undefined ||
                    formData.in_person_consultation_price !== undefined ||
                    formData.telemedicine_consultation_duration !== undefined ||
                    formData.telemedicine_consultation_price !== undefined ||
                    formData.telemedicine_url !== undefined ||
                    formData.exclude_FMP_inperson !== undefined ||
                    formData.exclude_FMP_telemedicine !== undefined
                )) {
                    // Only initialize if consultation details haven't been set yet
                    if (!consulationDetails || Object.keys(consulationDetails).length === 0) {
                        // Prices are now stored in dollars (float) in the DB, so use them directly
                        const inPersonPrice = formData.in_person_consultation_price;
                        const telemedicinePrice = formData.telemedicine_consultation_price;
                        
                        const consultationData = {
                            in_person_consultation_duration: formData.in_person_consultation_duration !== undefined && formData.in_person_consultation_duration !== null ? formData.in_person_consultation_duration : 15,
                            in_person_consultation_price: inPersonPrice ?? '',
                            telemedicine_consultation_duration: formData.telemedicine_consultation_duration !== undefined && formData.telemedicine_consultation_duration !== null ? formData.telemedicine_consultation_duration : 15,
                            telemedicine_consultation_price: telemedicinePrice ?? '',
                            telemedicine_url: formData.telemedicine_url || '',
                            exclude_FMP_inperson: formData.exclude_FMP_inperson === true,
                            exclude_FMP_telemedicine: formData.exclude_FMP_telemedicine === true,
                        };
                        setConsulationDetails(consultationData);
                    }
                }
                
                
                // Update consultation service states based on existing data
                const hasInPersonData = formData?.in_person_consultation_duration && formData?.in_person_consultation_price;
                const hasTelemedicineData = formData?.telemedicine_consultation_duration && formData?.telemedicine_consultation_price;
                const hasTelemedicineUrl = formData?.telemedicine_url && formData.telemedicine_url.length > 0;
                const hasTelemedicineUrlInConsulationDetails = consulationDetails?.telemedicine_url && consulationDetails.telemedicine_url.length > 0;
                // Treat telemedicine as "configured" in consulationDetails only when BOTH duration and price exist,
                // or when a URL is explicitly provided. This prevents the checkbox from auto-checking based on
                // default duration values alone.
                const hasTelemedicineInConsulationDetails =
                    (!!consulationDetails?.telemedicine_consultation_duration &&
                     !!consulationDetails?.telemedicine_consultation_price) ||
                    hasTelemedicineUrlInConsulationDetails;

                // If there is any telemedicine configuration (duration+price or URL),
                // we consider telemedicine "on" by default for edit mode.
                const hasAnyTelemedicineConfigured =
                    hasTelemedicineData || hasTelemedicineUrl || hasTelemedicineInConsulationDetails || currentTelemedicineState;
                
                // Set in-person state
                // Only auto-enable in-person when there is explicit in-person data.
                // Never auto-enable just because telemedicine is absent.
                if (!userUncheckedInPersonRef.current && hasInPersonData) {
                    setAddInPerson(true);
                    if (onConsultationCheckboxesChange) {
                        onConsultationCheckboxesChange({ addInPerson: true, addTelemedicine });
                    }
                }
                
                // Set telemedicine state
                // Check both formData and consulationDetails to prevent unchecking while user types in URL field
                const telemedicineUrlExists = hasTelemedicineUrl || hasTelemedicineUrlInConsulationDetails;
                
                // IMPORTANT: Only auto-check if checkbox is currently unchecked and there's telemedicine data
                // BUT don't auto-check if user just explicitly unchecked it
                // This prevents the checkbox from being auto-checked immediately after user unchecks it
                if (!addTelemedicine && !userUncheckedTelemedicineRef.current && 
                    (currentTelemedicineState || hasTelemedicineData || telemedicineUrlExists || hasTelemedicineInConsulationDetails)) {
                    // Auto-check only if there's existing telemedicine data, checkbox is unchecked, and user didn't just uncheck it
                    setAddTelemedicine(true);
                    if (onConsultationCheckboxesChange) {
                        onConsultationCheckboxesChange({ addInPerson, addTelemedicine: true });
                    }
                }
                // If addTelemedicine is already true, leave it as-is (don't modify user's explicit choice)
            }
        } else if (formState === 'add') {
            // Reset services when adding new provider (only once, not on every render)
            if (extraService.length === 0) {
                setExtraServicesCount(0);
                setExtraService([]);
                // Initialize with 15 minutes default for the first service row
                setExtraServiceDuration([15]);
                setExtraServicePrice([]);
                setExtraServiceTelemedecine([false]);
                setExtraServiceBookable([true]);
            }
            // Do not auto-enable in-person/telemedicine by default for new provider.
            // These will be driven either by explicit user action, or restored once per mount
            // from any existing consultation details in consulationDetails (see effect below).
        }
    }, [formData, formState, consulationDetails]);

    // For ADD mode: when coming back to the Services step (component remount),
    // restore the In-Person / Telemedicine consultation toggles based on any
    // existing consultation data that was previously entered and persisted in
    // consulationDetails. This runs only once per mount to avoid fighting
    // with the user's explicit checkbox changes.
    useEffect(() => {
        if (formState !== 'add') return;
        if (consultationTogglesInitializedRef.current) return;

        // First, try to restore from explicitly persisted toggle choices
        const storedAddInPerson =
            additionalMultipleFields && typeof additionalMultipleFields.addInPersonConsultation === 'boolean'
                ? additionalMultipleFields.addInPersonConsultation
                : null;
        const storedAddTelemedicine =
            additionalMultipleFields && typeof additionalMultipleFields.addTelemedicineConsultation === 'boolean'
                ? additionalMultipleFields.addTelemedicineConsultation
                : null;

        if (storedAddInPerson !== null || storedAddTelemedicine !== null) {
            if (storedAddInPerson !== null) {
                setAddInPerson(storedAddInPerson);
            }
            if (storedAddTelemedicine !== null) {
                setAddTelemedicine(storedAddTelemedicine);
            }
            consultationTogglesInitializedRef.current = true;
            return;
        }

        // Fallback: infer from existing consultation data if toggles were not
        // explicitly stored (e.g. older drafts).
        if (!consulationDetails || Object.keys(consulationDetails).length === 0) {
            consultationTogglesInitializedRef.current = true;
            return;
        }

        const hasInPersonData =
            !!consulationDetails?.in_person_consultation_duration &&
            !!consulationDetails?.in_person_consultation_price;

        const hasTelemedicineData =
            !!consulationDetails?.telemedicine_consultation_duration &&
            !!consulationDetails?.telemedicine_consultation_price;

        const hasTelemedicineUrl =
            !!consulationDetails?.telemedicine_url &&
            consulationDetails.telemedicine_url.length > 0;

        const shouldEnableInPerson = !!hasInPersonData;
        const shouldEnableTelemedicine = !!(hasTelemedicineData || hasTelemedicineUrl);

        if (shouldEnableInPerson) {
            setAddInPerson(true);
        }
        if (shouldEnableTelemedicine) {
            setAddTelemedicine(true);
        }

        consultationTogglesInitializedRef.current = true;
    }, [formState, consulationDetails, additionalMultipleFields]);

    // Ensure consulationDetails values are safe to access
    // Check both consulationDetails state and formData as fallback
    const getConsulationValue = (key, defaultValue = '') => {
        // First check consulationDetails state, then formData as fallback
        const value = consulationDetails && consulationDetails[key] !== undefined 
            ? consulationDetails[key] 
            : (formData && formData[key] !== undefined ? formData[key] : defaultValue);
        
        // For price fields, format the value
        if (key.includes('_price')) {
            // If value is 0 or empty, return empty string to show placeholder
            if (value === 0 || value === '0' || value === '0.00' || !value || value === '') {
                return '';
            }
            // If it's already a formatted string (has decimal), return as is
            if (typeof value === 'string' && value.includes('.')) {
                return value;
            }
            // Convert to number then format with 2 decimal places
            const numValue = typeof value === 'number' ? value : parseFloat(value);
            if (!isNaN(numValue)) {
                return numValue.toFixed(2);
            }
            // Fallback: return as string
            return String(value);
        }
        
        // For duration fields, return the value as is (or convert to string for select inputs)
        if (key.includes('_duration')) {
            if (value === 0 || value === '0' || !value || value === '') {
                // If no value and we have a default from formData, use that
                const formDataValue = formData && formData[key] !== undefined ? formData[key] : null;
                if (formDataValue && formDataValue !== 0) {
                    return String(formDataValue);
                }
                // Default to 15 for new providers (when in add mode)
                if (formState === 'add' && defaultValue === '') {
                    return '15';
                }
                return defaultValue;
            }
            // Convert to string if it's a number (select inputs need string values)
            return String(value);
        }
        
        return value || defaultValue;
    };

    // Handle price focus - set to 0.00 and select for easy editing
    const handlePriceFocus = (e, priceKey) => {
        const currentValue = consulationDetails[priceKey];
        if (!currentValue || currentValue === '') {
            setConsulationDetails({
                ...consulationDetails,
                [priceKey]: '0.00'
            });
            setTimeout(() => {
                e.target.select();
            }, 0);
        } else if (currentValue === '0.00') {
            setTimeout(() => {
                e.target.select();
            }, 0);
        }
    };

    // Handle price change with currency formatting
    const handlePriceChange = (e, priceKey) => {
        const { value } = e.target;

        // Remove all non-numeric characters
        const numericValue = value.replace(/[^0-9]/g, '');

        // Format as currency: convert to cents, then format back
        let finalValue = '';
        if (numericValue) {
            // Convert to number and divide by 100 to get dollars
            const cents = parseInt(numericValue, 10);
            const dollars = cents / 100;
            finalValue = dollars.toFixed(2);
        }

        // Clear the error for this field when user edits it
        if (onClearConsultationPriceError) {
            if (priceKey === 'in_person_consultation_price') onClearConsultationPriceError('inPerson');
            if (priceKey === 'telemedicine_consultation_price') onClearConsultationPriceError('telemedicine');
        }

        setConsulationDetails({
            ...consulationDetails,
            [priceKey]: finalValue
        });
    };

    return (
        <>
            <Grid item xs={12}>
                <FormHeading>Default Consultation Services</FormHeading>
            </Grid>
            <Grid item xs={12} style={{ marginTop: '-8px', marginBottom: '24px' }}>
                <Typography variant="body2" style={{ color: '#999', fontSize: '14px', lineHeight: '1.5', marginBottom: '8px' }}>
                    Patients book these appointment types by default. Configure which appointment types your provider offers by checking the boxes below. You can also uncheck both consultation options and only use the individual services below if you don't want to enable appointment booking but want to show your services.
                </Typography>
                <Box style={{ backgroundColor: '#F5F1ED', padding: '12px', paddingLeft: '16px', paddingRight: '16px', borderRadius: '8px', marginTop: '16px' }}>
                    <Typography variant="body2" style={{ color: '#79716B', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                        Configuration Options:
                    </Typography>
                    <Typography variant="body2" style={{ color: '#79716B', fontSize: '12px', lineHeight: '1.6' }}>
                        • <strong>In-person only:</strong> Check In-Person, uncheck Telemedicine<br/>
                        • <strong>Telemedicine only:</strong> Check Telemedicine, uncheck In-Person<br/>
                        • <strong>Both:</strong> Check both options<br/>
                        • <strong>Individual services only:</strong> Uncheck both options and use services below (no appointment booking, services shown to patients)
                    </Typography>
                </Box>
            </Grid>
            
            <div style={{ marginBottom: '34px', marginLeft: '-30px', marginRight: '-30px' }}>
                <Grid container style={{ paddingLeft: '30px', paddingRight: '30px' }}>
                    {/* In-Person Consultation */}
                    <Grid item xs={12} md={6} style={{ paddingLeft: '18px', paddingRight: '12px' }}>
                    <Box style={{ 
                        border: '1px solid #E1E1E1', 
                        borderRadius: '8px', 
                        padding: '16px',
                        opacity: addInPerson ? 1 : 0.6,
                        backgroundColor: addInPerson ? 'white' : '#FAF9F7'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                            <div 
                                onClick={handleToggleInPerson}
                                style={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '4px',
                                    border: addInPerson ? '1px solid #75BD2F' : '1px solid #cbc1b7',
                                    backgroundColor: addInPerson ? '#75BD2F' : '#f5f1ed',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: '10px',
                                    transition: 'all 0.3s ease',
                                    flexShrink: 0
                                }}
                            >
                                {addInPerson && (
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white" stroke="white" strokeWidth="1.5" />
                                    </svg>
                                )}
                            </div>
                            <FormInputLabel label={'In-Person Consultation'} labelSize={12} labelBottomMargin={'0'} style={{ margin: 0 }} />
                        </div>
                        
                        {addInPerson && (
                            <>
                                <FormSelectInput
                                    name="in_person_consultation_duration"
                                    value={getConsulationValue('in_person_consultation_duration')}
                                    handleChange={handleConsulationChange}
                                    required={true}
                                    inputSize={12}
                                    placeholder={'Select a time duration'}
                                    options={serviceTimeOptions}
                                    optionKey="id"
                                    optionValue={['service']}
                                    noLabel={true}
                                />
                                <FormTextInput
                                    style={{ marginBottom: 0 }}
                                    value={getConsulationValue('in_person_consultation_price')}
                                    label="Price"
                                    name="in_person_consultation_price"
                                    error={consultationPriceErrors.inPerson ? 'Price must be at least $25' : undefined}
                                    handleChange={(e) => handlePriceChange(e, 'in_person_consultation_price')}
                                    placeholder="0.00"
                                    subText=""
                                    required={true}
                                    index={1}
                                    type="number"
                                    inputProps={{
                                        step: "0.01",
                                        min: "0",
                                        inputMode: "decimal",
                                        style: { textAlign: 'right' },
                                        onFocus: (e) => handlePriceFocus(e, 'in_person_consultation_price'),
                                        onKeyDown: (ev) => {
                                            const allowed = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Home','End'];
                                            if (allowed.includes(ev.key)) return;
                                            const isDigit = /[0-9]/.test(ev.key);
                                            if (!isDigit) {
                                                ev.preventDefault();
                                                return;
                                            }
                                        },
                                        onPaste: (ev) => {
                                            const paste = (ev.clipboardData || window.clipboardData).getData('text');
                                            if (!/^\d*$/.test(paste)) {
                                                ev.preventDefault();
                                            }
                                        }
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <Box
                                                sx={{
                                                    backgroundColor: 'rgba(203, 193, 183, 1)',
                                                    color: 'rgba(87, 83, 78, 1)',
                                                    height: '42px',
                                                    width: '42px !important',
                                                    minWidth: '42px !important',
                                                    maxWidth: '42px !important',
                                                    flexBasis: '42px !important',
                                                    flexGrow: '0 !important',
                                                    flexShrink: '0 !important',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    marginLeft: '-14px',
                                                    borderTopLeftRadius: '8px',
                                                    borderBottomLeftRadius: '8px',
                                                }}
                                            >
                                                <AttachMoney fontSize="small" />
                                            </Box>
                                        ),
                                        sx: {
                                            '& input[type=number]': {
                                                MozAppearance: 'textfield',
                                                textAlign: 'right',
                                                borderTopLeftRadius: '0px',
                                                borderBottomLeftRadius: '0px',
                                            },
                                            '& input[type=number]:hover': {
                                                borderTopLeftRadius: '0px',
                                                borderBottomLeftRadius: '0px',
                                            },
                                            '& input[type=number]:focus': {
                                                borderTopLeftRadius: '0px',
                                                borderBottomLeftRadius: '0px',
                                            },
                                            '& input[type=number]:focus-visible': {
                                                borderTopLeftRadius: '0px',
                                                borderBottomLeftRadius: '0px',
                                            },
                                            '& input[type=number]:focus-within': {
                                                borderTopLeftRadius: '0px',
                                                borderBottomLeftRadius: '0px',
                                            },
                                            '& fieldset': {
                                                borderTopLeftRadius: '0px',
                                                borderBottomLeftRadius: '0px',
                                            },
                                            '& fieldset:hover': {
                                                borderTopLeftRadius: '0px',
                                                borderBottomLeftRadius: '0px',
                                            },
                                            '& fieldset:focus': {
                                                borderTopLeftRadius: '0px',
                                                borderBottomLeftRadius: '0px',
                                            },
                                            '& .MuiInputAdornment-root': {
                                                maxWidth: 'none !important',
                                                minWidth: '42px !important',
                                                width: '42px !important',
                                                flexBasis: '42px !important',
                                                flexGrow: '0 !important',
                                                flexShrink: '0 !important',
                                            },
                                            '& input[type=number]::-webkit-outer-spin-button': {
                                                WebkitAppearance: 'none',
                                                margin: 0,
                                            },
                                            '& input[type=number]::-webkit-inner-spin-button': {
                                                WebkitAppearance: 'none',
                                                margin: 0,
                                            },
                                        }
                                    }}
                                    noLabel={true}
                                    textSize={12}
                                />
                                <div style={{ display: 'flex', alignItems: 'center', marginTop: '16px', gap: '12px' }}>
                                    <div
                                        role="switch"
                                        tabIndex={0}
                                        aria-checked={consulationDetails?.exclude_FMP_inperson === true}
                                        onClick={() => setConsulationDetails({ ...consulationDetails, exclude_FMP_inperson: !(consulationDetails?.exclude_FMP_inperson === true) })}
                                        onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setConsulationDetails({ ...consulationDetails, exclude_FMP_inperson: !(consulationDetails?.exclude_FMP_inperson === true) }); } }}
                                        style={{
                                            width: 44,
                                            height: 24,
                                            borderRadius: 12,
                                            backgroundColor: consulationDetails?.exclude_FMP_inperson ? '#75BD2F' : '#cbc1b7',
                                            cursor: 'pointer',
                                            flexShrink: 0,
                                            position: 'relative',
                                            transition: 'background-color 0.2s ease',
                                        }}
                                    >
                                        <span
                                            style={{
                                                position: 'absolute',
                                                top: 2,
                                                left: consulationDetails?.exclude_FMP_inperson ? 22 : 2,
                                                width: 20,
                                                height: 20,
                                                borderRadius: '50%',
                                                backgroundColor: '#fff',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                                transition: 'left 0.2s ease',
                                            }}
                                        />
                                    </div>
                                    <span style={{ fontSize: '14px', color: '#3E3E4A', fontFamily: 'Poppins, sans-serif', fontWeight: 500, cursor: 'pointer' }}
                                        onClick={() => setConsulationDetails({ ...consulationDetails, exclude_FMP_inperson: !(consulationDetails?.exclude_FMP_inperson === true) })}
                                    >
                                        Excluded from Federal Medical Programs
                                    </span>
                                </div>
                            </>
                        )}
                </Box>
            </Grid>

                    {/* Telemedicine Consultation */}
                    <Grid item xs={12} md={6} style={{ paddingLeft: '12px', paddingRight: '0' }}>
                <Box style={{ 
                        border: '1px solid #E1E1E1', 
                        borderRadius: '8px', 
                        padding: '16px',
                        opacity: addTelemedicine ? 1 : 0.6,
                        backgroundColor: addTelemedicine ? 'white' : '#FAF9F7'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                            <div 
                                onClick={handleToggleTelemedicine}
                                style={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '4px',
                                    border: addTelemedicine ? '1px solid #75BD2F' : '1px solid #cbc1b7',
                                    backgroundColor: addTelemedicine ? '#75BD2F' : '#f5f1ed',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: '10px',
                                    transition: 'all 0.3s ease',
                                    flexShrink: 0
                                }}
                            >
                                {addTelemedicine && (
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="white" stroke="white" strokeWidth="1.5" />
                                    </svg>
                                )}
                            </div>
                            <FormInputLabel label={'Telemedicine Consultation'} labelSize={12} labelBottomMargin={'0'} style={{ margin: 0 }} />
                        </div>
                        
                        {addTelemedicine && (
                            <>
                                <FormSelectInput
                                    name="telemedicine_consultation_duration"
                                    value={getConsulationValue('telemedicine_consultation_duration')}
                                    handleChange={handleConsulationChange}
                                    required={true}
                                    inputSize={12}
                                    placeholder={'Select a time duration'}
                                    options={serviceTimeOptions}
                                    optionKey="id"
                                    optionValue={['service']}
                                    noLabel={true}
                                />
                                <FormTextInput
                                    value={getConsulationValue('telemedicine_consultation_price')}
                                    label="Price"
                                    name="telemedicine_consultation_price"
                                    error={consultationPriceErrors.telemedicine ? 'Price must be at least $25' : undefined}
                                    handleChange={(e) => handlePriceChange(e, 'telemedicine_consultation_price')}
                                    placeholder="0.00"
                                    subText=""
                                    required={true}
                                    index={1}
                                    type="number"
                                        inputProps={{
                                            step: "0.01",
                                            min: "0",
                                            inputMode: "decimal",
                                            style: { textAlign: 'right' },
                                            onFocus: (e) => handlePriceFocus(e, 'telemedicine_consultation_price'),
                                            onKeyDown: (ev) => {
                                                const allowed = ['Backspace','Delete','ArrowLeft','ArrowRight','Tab','Home','End'];
                                                if (allowed.includes(ev.key)) return;
                                                const isDigit = /[0-9]/.test(ev.key);
                                                if (!isDigit) {
                                                    ev.preventDefault();
                                                    return;
                                                }
                                            },
                                            onPaste: (ev) => {
                                                const paste = (ev.clipboardData || window.clipboardData).getData('text');
                                                if (!/^\d*$/.test(paste)) {
                                                    ev.preventDefault();
                                                }
                                            }
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <Box
                                                    sx={{
                                                        backgroundColor: 'rgba(203, 193, 183, 1)',
                                                        color: 'rgba(87, 83, 78, 1)',
                                                        height: '42px',
                                                        width: '42px !important',
                                                        minWidth: '42px !important',
                                                        maxWidth: '42px !important',
                                                        flexBasis: '42px !important',
                                                        flexGrow: '0 !important',
                                                        flexShrink: '0 !important',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        marginLeft: '-14px',
                                                        borderTopLeftRadius: '7px',
                                                        borderBottomLeftRadius: '7px',
                                                    }}
                                                >
                                                    <AttachMoney fontSize="small" />
                                                </Box>
                                            ),
                                            sx: {
                                                '& input[type=number]': {
                                                    MozAppearance: 'textfield',
                                                    textAlign: 'right',
                                                    borderTopLeftRadius: '0px',
                                                    borderBottomLeftRadius: '0px',
                                                },
                                                '& input[type=number]:hover': {
                                                    borderTopLeftRadius: '0px',
                                                    borderBottomLeftRadius: '0px',
                                                },
                                                '& input[type=number]:focus': {
                                                    borderTopLeftRadius: '0px',
                                                    borderBottomLeftRadius: '0px',
                                                },
                                                '& input[type=number]:focus-visible': {
                                                    borderTopLeftRadius: '0px',
                                                    borderBottomLeftRadius: '0px',
                                                },
                                                '& input[type=number]:focus-within': {
                                                    borderTopLeftRadius: '0px',
                                                    borderBottomLeftRadius: '0px',
                                                },
                                                '& fieldset': {
                                                    borderTopLeftRadius: '0px',
                                                    borderBottomLeftRadius: '0px',
                                                },
                                                '& fieldset:hover': {
                                                    borderTopLeftRadius: '0px',
                                                    borderBottomLeftRadius: '0px',
                                                },
                                                '& fieldset:focus': {
                                                    borderTopLeftRadius: '0px',
                                                    borderBottomLeftRadius: '0px',
                                                },
                                                '& .MuiInputAdornment-root': {
                                                    maxWidth: 'none !important',
                                                    minWidth: '42px !important',
                                                    width: '42px !important',
                                                    flexBasis: '42px !important',
                                                    flexGrow: '0 !important',
                                                    flexShrink: '0 !important',
                                                },
                                                '& input[type=number]::-webkit-outer-spin-button': {
                                                    WebkitAppearance: 'none',
                                                    margin: 0,
                                                },
                                                '& input[type=number]::-webkit-inner-spin-button': {
                                                    WebkitAppearance: 'none',
                                                    margin: 0,
                                                },
                                            }
                                        }}
                                    noLabel={true}
                                    textSize={12}
                                />
                                <FormTextInput
                                    style={{ marginBottom: 0 }}
                                    formData={formData}
                                    label=""
                                    name="telemedicine_url"
                                        handleChange={(e) => {
                                            // Update both formData and consulationDetails when URL changes
                                            handleChange(e);
                                            setConsulationDetails({
                                                ...consulationDetails,
                                                telemedicine_url: e.target.value
                                            });
                                        }}
                                        placeholder="Enter telemed. URL"
                                        required={false}
                                        labelSize={12}
                                        textSize={12}
                                        type={'url'}
                                    labelBottomMargin={'-mb-1'}
                                    noLabel={true}
                                    inputProps={{ autoComplete: 'off' }}
                                />
                                <div style={{ display: 'flex', alignItems: 'center', marginTop: '16px', gap: '12px' }}>
                                    <div
                                        role="switch"
                                        tabIndex={0}
                                        aria-checked={consulationDetails?.exclude_FMP_telemedicine === true}
                                        onClick={() => setConsulationDetails({ ...consulationDetails, exclude_FMP_telemedicine: !(consulationDetails?.exclude_FMP_telemedicine === true) })}
                                        onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setConsulationDetails({ ...consulationDetails, exclude_FMP_telemedicine: !(consulationDetails?.exclude_FMP_telemedicine === true) }); } }}
                                        style={{
                                            width: 44,
                                            height: 24,
                                            borderRadius: 12,
                                            backgroundColor: consulationDetails?.exclude_FMP_telemedicine ? '#75BD2F' : '#cbc1b7',
                                            cursor: 'pointer',
                                            flexShrink: 0,
                                            position: 'relative',
                                            transition: 'background-color 0.2s ease',
                                        }}
                                    >
                                        <span
                                            style={{
                                                position: 'absolute',
                                                top: 2,
                                                left: consulationDetails?.exclude_FMP_telemedicine ? 22 : 2,
                                                width: 20,
                                                height: 20,
                                                borderRadius: '50%',
                                                backgroundColor: '#fff',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                                transition: 'left 0.2s ease',
                                            }}
                                        />
                                    </div>
                                    <span style={{ fontSize: '14px', color: '#3E3E4A', fontFamily: 'Poppins, sans-serif', fontWeight: 500, cursor: 'pointer' }}
                                        onClick={() => setConsulationDetails({ ...consulationDetails, exclude_FMP_telemedicine: !(consulationDetails?.exclude_FMP_telemedicine === true) })}
                                    >
                                        Excluded from Federal Medical Programs
                                    </span>
                                </div>
                            </>
                        )}
                    </Box>
                </Grid>
                </Grid>
            </div>

            <Grid item xs={12}>
                <Box
                    sx={{
                        borderBottom: '1px solid',
                        borderColor: theme.colorTableHeaderBackground,
                        paddingBottom: '10px',
                        marginBottom: '12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%'
                    }}
                >
                    <Box
                        sx={{
                            color: theme.secondaryFontColor,
                            fontWeight: 700,
                            fontSize: '18px',
                            fontFamily: 'Poppins'
                        }}
                    >
                        Individual Services
                    </Box>
                    <Box
                        className={'primary-link cursor-pointer'}
                        onClick={() => {
                            setExtraServicesCount(extraServicesCount + 1);
                            const currentTelemedecine = Array.isArray(extraServiceTelemedecine) && extraServiceTelemedecine.length > 0 ? extraServiceTelemedecine : [addTelemedicine || false];
                            const currentBookable = Array.isArray(extraServiceBookable) && extraServiceBookable.length > 0 ? extraServiceBookable : [true];
                            setExtraServiceTelemedecine([...currentTelemedecine, addTelemedicine || false]);
                            setExtraServiceBookable([...currentBookable, true]);
                        }}
                    >
                        + Add Another Service
                    </Box>
                </Box>
            </Grid>
            <Grid item xs={12} style={{ marginTop: '-16px', marginBottom: '16px' }}>
                <Typography variant="body2" style={{ color: '#999', fontSize: '14px', lineHeight: '1.5' }}>
                    Add specific services you offer beyond general consultations. These can include specialized treatments, procedures, or additional services with their own pricing and duration. Use the service availability dropdown to control how each service can be accessed—select "In-Person & Telemedicine", "In-Person only", or "Telemedicine only" to make the service available for purchase, or select "Not offered" to display the service and price to patients but disable their ability to purchase it from the marketplace.
                </Typography>
            </Grid>

            {(() => {
                const totalServices = extraServicesCount + 1; // First service + additional services
                const showRemoveLink = totalServices > 1;

                const createRemoveHandler = (serviceIndexToRemove) => {
                    if (!showRemoveLink) {
                        return null;
                    }

                    return () => {
                        const newServices = Array.isArray(extraService) ? [...extraService] : [];
                        const newServiceDuration = Array.isArray(extraServiceDuration) ? [...extraServiceDuration] : [];
                        const newServicePrice = Array.isArray(extraServicePrice) ? [...extraServicePrice] : [];
                        const newServiceTelemedicine = Array.isArray(extraServiceTelemedecine) ? [...extraServiceTelemedecine] : [];
                        const newServiceBookable = Array.isArray(extraServiceBookable) ? [...extraServiceBookable] : [];
                        
                        if (serviceIndexToRemove === 0) {
                            // Removing the first service - shift all other services up
                            newServices.shift();
                            newServiceDuration.shift();
                            newServicePrice.shift();
                            newServiceTelemedicine.shift();
                            newServiceBookable.shift();
                            
                            setExtraService(newServices);
                            setExtraServiceDuration(newServiceDuration);
                            setExtraServicePrice(newServicePrice);
                            setExtraServiceTelemedecine(newServiceTelemedicine);
                            setExtraServiceBookable(newServiceBookable);
                            
                            // Decrement count of additional services
                            if (extraServicesCount > 0) {
                                setExtraServicesCount(extraServicesCount - 1);
                            }
                        } else {
                            // Removing an additional service (index > 0)
                            const actualIndexToRemove = serviceIndexToRemove;
                            if (newServices.length > actualIndexToRemove) {
                                newServices.splice(actualIndexToRemove, 1);
                            }
                            if (newServiceDuration.length > actualIndexToRemove) {
                                newServiceDuration.splice(actualIndexToRemove, 1);
                            }
                            if (newServicePrice.length > actualIndexToRemove) {
                                newServicePrice.splice(actualIndexToRemove, 1);
                            }
                            if (newServiceTelemedicine.length > actualIndexToRemove) {
                                newServiceTelemedicine.splice(actualIndexToRemove, 1);
                            }
                            if (newServiceBookable.length > actualIndexToRemove) {
                                newServiceBookable.splice(actualIndexToRemove, 1);
                            }
                            
                            setExtraService(newServices);
                            setExtraServiceDuration(newServiceDuration);
                            setExtraServicePrice(newServicePrice);
                            setExtraServiceTelemedecine(newServiceTelemedicine);
                            setExtraServiceBookable(newServiceBookable);
                            
                            // Decrement count of additional services
                            setExtraServicesCount(extraServicesCount - 1);
                        }
                        
                        setAdditionalMultipleFields((prev) => ({
                            ...prev,
                            services: newServices,
                            service_duration: newServiceDuration,
                            service_price: newServicePrice,
                            service_telemedecine: newServiceTelemedicine,
                            service_bookable: newServiceBookable,
                        }));
                    };
                };

                        return (
                            <>
                                {/* Render additional services in reverse order (newest first) */}
                                {Array.from({ length: extraServicesCount }).reverse().map((_, reverseIndex) => {
                                    const index = extraServicesCount - 1 - reverseIndex; // Convert reverse index back to original index
                                    const actualIndexToRemove = index + 1;
                                    return (
                                        <Services
                                            key={`service-${index + 1}-${extraServicesCount}`}
                                            formData={formData}
                                            handleChange={handelServiceChange(index + 1)}
                                            extraService={extraService}
                                            extraServiceDuration={extraServiceDuration}
                                            extraServicePrice={extraServicePrice}
                                            setExtraServicePrice={setExtraServicePrice}
                                            extraServiceTelemedecine={extraServiceTelemedecine}
                                            extraServiceBookable={extraServiceBookable}
                                            count={index + 1}
                                            servicesOptions={servicesOptions}
                                            addTelemedicine={addTelemedicine}
                                            onServicePriceAutoPopulate={handleServicePriceAutoPopulate}
                                            onAddService={onAddService}
                                            additionalMultipleFields={additionalMultipleFields}
                                            setAdditionalMultipleFields={setAdditionalMultipleFields}
                                            onRemoveService={showRemoveLink ? createRemoveHandler(actualIndexToRemove) : null}
                                        />
                                    );
                                })}
                                {/* Render first service last */}
                                <Services
                                    key="service-0"
                                    count={0}
                                    formData={formData}
                                    handleChange={handelServiceChange(0)}
                                    extraService={extraService}
                                    extraServiceDuration={extraServiceDuration}
                                    extraServicePrice={extraServicePrice}
                                    setExtraServicePrice={setExtraServicePrice}
                                    extraServiceTelemedecine={extraServiceTelemedecine}
                                    extraServiceBookable={extraServiceBookable}
                                    servicesOptions={servicesOptions}
                                    addTelemedicine={addTelemedicine}
                                    onServicePriceAutoPopulate={handleServicePriceAutoPopulate}
                                    onAddService={handleAddService}
                                    additionalMultipleFields={additionalMultipleFields}
                                    setAdditionalMultipleFields={setAdditionalMultipleFields}
                                    onRemoveService={showRemoveLink ? createRemoveHandler(0) : null}
                                />
                            </>
                        );
            })()}
        </>
    );
}
